import { parseHex, readableInk } from './readable-ink'
/**
 * The colours a category may be given — `BACKEND.md §10A.7`, `FRONTEND.md §9.15`.
 *
 * The same twelve the backend's `CategoryColours.PALETTE` accepts, in the same order. They are
 * duplicated rather than fetched because a colour picker that cannot render until a request returns is
 * worse than one list written twice — and `categoryColourProblem` below is what stops the two drifting
 * silently: an unknown colour renders as none rather than as a broken chip.
 *
 * Tailwind's 500 step for each hue, chosen for separation at a glance on a till used at speed.
 */
export const CATEGORY_PALETTE = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#eab308',
  '#84cc16',
  '#22c55e',
  '#14b8a6',
  '#06b6d4',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#78716c',
] as const

/**
 * A category's colour: one of the twelve, or any `#rrggbb` — §9.27, owner decision 2026-08-05.
 *
 * Deliberately `string` rather than a union with the palette. It was the union until free hex was
 * allowed, and keeping both would say the twelve are special to the type system when they are only a
 * list of suggestions — `categoryColour` is the gate, and it accepts any six-digit hex.
 */
export type CategoryColour = string

const ALLOWED = new Set<string>(CATEGORY_PALETTE)

/**
 * A category's colour, or `null` when it has none — or has one this build does not know.
 *
 * An unrecognised value is treated as *no colour* rather than rendered: a chip with an arbitrary
 * background is how an unreadable label reaches a till, and the category still shows with its default
 * styling, which is exactly what it looked like before anybody chose a colour.
 */
export function categoryColour(value: unknown): CategoryColour | null {
  if (typeof value !== 'string') return null
  const lower = value.trim().toLowerCase()
  if (ALLOWED.has(lower)) return lower
  // §9.27, owner decision 2026-08-05: any six-digit hex, not only the twelve. The palette is
  // suggestions now rather than a whitelist — what keeps a chip readable is `readableInk`, not a
  // short list. A value that is not a colour at all is still no colour.
  return parseHex(lower) === null ? null : lower
}

/**
 * The styles a coloured chip takes: the colour as a tint, and the colour itself for the text.
 *
 * A tint rather than a solid fill. The chips sit next to the selected-category highlight, and filling
 * twelve of them solid would leave nothing for *selected* to mean — the thing a waitress actually needs
 * to see mid-order is which one she is in, not how many exist.
 */
export function categoryChipStyle(
  colour: CategoryColour | null,
  onDark = false,
): Record<string, string> {
  if (colour === null) return {}
  return {
    backgroundColor: `${colour}1a`,
    borderColor: `${colour}66`,
    // §9.27 — the chosen colour, moved along its own hue until it reads. The tint and the border keep
    // the colour as picked; only the text is adjusted, and only when it has to be.
    color: readableInk(colour, onDark),
  }
}
